import { useMemo } from 'react';
import { PricePoint } from '../types';

interface Props {
  points: PricePoint[];
  currency?: string;
  width?: number;
  height?: number;
}

export function PriceHistoryGraph({ points, currency = 'EUR', width = 420, height = 140 }: Props) {
  const filtered = useMemo(
    () => points.filter((p) => p.currency === currency).slice(-60).sort(
      (a, b) => new Date(a.recordedAt).getTime() - new Date(b.recordedAt).getTime(),
    ),
    [points, currency],
  );

  if (filtered.length < 2) {
    return (
      <div style={{ padding: '20px', textAlign: 'center', color: '#8a93b0', fontSize: '12px', background: '#161820', borderRadius: '8px' }}>
        Not enough price data yet — check back after the first search.
      </div>
    );
  }

  const pad = { top: 16, right: 12, bottom: 28, left: 44 };
  const innerW = width - pad.left - pad.right;
  const innerH = height - pad.top - pad.bottom;

  const prices = filtered.map((p) => p.price);
  const minP = Math.min(...prices);
  const maxP = Math.max(...prices);
  const priceRange = maxP - minP || 1;

  const times = filtered.map((p) => new Date(p.recordedAt).getTime());
  const minT = times[0];
  const maxT = times[times.length - 1];
  const timeRange = maxT - minT || 1;

  const toX = (t: number) => pad.left + ((t - minT) / timeRange) * innerW;
  const toY = (p: number) => pad.top + innerH - ((p - minP) / priceRange) * innerH;

  const points2d = filtered.map((pt, i) => ({ x: toX(times[i]), y: toY(pt.price), pt }));

  const pathD = points2d.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');
  const fillD = `${pathD} L${points2d[points2d.length - 1].x.toFixed(1)},${(pad.top + innerH).toFixed(1)} L${pad.left},${(pad.top + innerH).toFixed(1)} Z`;

  // Y-axis labels: 3 ticks
  const yTicks = [minP, (minP + maxP) / 2, maxP];
  // X-axis labels: first and last
  const xLabels = [
    { x: toX(minT), label: shortDate(filtered[0].recordedAt) },
    { x: toX(maxT), label: shortDate(filtered[filtered.length - 1].recordedAt) },
  ];

  // Tooltip hover target: last point
  const last = points2d[points2d.length - 1];

  return (
    <div style={{ position: 'relative' }}>
      <svg width={width} height={height} style={{ overflow: 'visible', display: 'block' }}>
        <defs>
          <linearGradient id="pg" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#f97316" stopOpacity="0.3" />
            <stop offset="100%" stopColor="#f97316" stopOpacity="0.02" />
          </linearGradient>
        </defs>

        {/* Grid lines */}
        {yTicks.map((v, i) => (
          <line
            key={i}
            x1={pad.left} y1={toY(v).toFixed(1)}
            x2={pad.left + innerW} y2={toY(v).toFixed(1)}
            stroke="#2e3347" strokeWidth="1"
          />
        ))}

        {/* Fill area */}
        <path d={fillD} fill="url(#pg)" />

        {/* Line */}
        <path d={pathD} fill="none" stroke="#f97316" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />

        {/* Data points */}
        {points2d.map((p, i) => (
          <circle key={i} cx={p.x} cy={p.y} r="3" fill="#f97316" />
        ))}

        {/* Last value label */}
        <text x={last.x + 6} y={last.y + 4} fill="#f97316" fontSize="11" fontWeight="700">
          {currency === 'EUR' ? '€' : currency === 'GBP' ? '£' : '$'}{last.pt.price.toFixed(2)}
        </text>

        {/* Y-axis labels */}
        {yTicks.map((v, i) => (
          <text key={i} x={pad.left - 6} y={toY(v) + 4} fill="#8a93b0" fontSize="10" textAnchor="end">
            {v.toFixed(0)}
          </text>
        ))}

        {/* X-axis labels */}
        {xLabels.map(({ x, label }, i) => (
          <text key={i} x={x} y={pad.top + innerH + 16} fill="#8a93b0" fontSize="10" textAnchor="middle">
            {label}
          </text>
        ))}
      </svg>
    </div>
  );
}

function shortDate(iso: string): string {
  const d = new Date(iso);
  return `${d.getDate()}/${d.getMonth() + 1}`;
}
